import { useEffect, useId, useRef } from 'react';
import { useI18n } from 'core/i18n/I18nProvider';
import {
  SPECIALIST_AVATAR_ACCEPT,
  validateSpecialistAvatarFile,
} from 'features/career-specialists/specialists.helpers';
import { formatFileSize } from 'shared/utilities/format';
import { SpecialistAvatar } from 'features/career-specialists/SpecialistAvatar';

export function SpecialistAvatarField({
  existingUrl,
  file,
  previewUrl,
  removeRequested,
  onPickFile,
  onRemove,
  onClearPick,
  error,
  disabled,
  specialistForFallback,
}) {
  const { t, locale } = useI18n();
  const inputId = useId();
  const inputRef = useRef(null);
  const hintId = `${inputId}-hint`;
  const errorId = error ? `${inputId}-error` : undefined;
  const describedBy = [hintId, errorId].filter(Boolean).join(' ') || undefined;
  const displayUrl = removeRequested ? '' : (previewUrl || existingUrl || '');
  const showCurrent = Boolean(displayUrl);

  useEffect(() => () => {
    if (previewUrl?.startsWith('blob:')) {
      URL.revokeObjectURL(previewUrl);
    }
  }, [previewUrl]);

  const handleChange = (event) => {
    const next = event.target.files?.[0] || null;
    if (!next) {
      onClearPick();
      return;
    }
    const clientError = validateSpecialistAvatarFile(next, t);
    if (clientError) {
      onPickFile(null, clientError);
      event.target.value = '';
      return;
    }
    onPickFile(next, null);
  };

  return (
    <fieldset className="field specialist-avatar-field" disabled={disabled} aria-describedby={describedBy}>
      <legend className="field-label">{t('specialists.uploadImage')}</legend>

      <div className="specialist-avatar-preview-row">
        {showCurrent ? (
          <img
            className="avatar file-preview"
            src={displayUrl}
            alt={t('specialists.currentImage')}
            width={64}
            height={64}
            onError={(event) => {
              event.currentTarget.hidden = true;
            }}
          />
        ) : (
          <SpecialistAvatar specialist={specialistForFallback} size={64} />
        )}
      </div>

      {!disabled ? (
        <>
          <input
            ref={inputRef}
            id={inputId}
            className={error ? 'input input-error file-input' : 'input file-input'}
            type="file"
            accept={SPECIALIST_AVATAR_ACCEPT}
            onChange={handleChange}
            aria-label={t('specialists.uploadImage')}
            aria-invalid={Boolean(error)}
            aria-describedby={describedBy}
            disabled={disabled}
          />
          {file ? (
            <p className="field-hint">
              {file.name} · {formatFileSize(file.size, locale)}
            </p>
          ) : null}
          <div className="toolbar wrap">
            <button
              type="button"
              className="btn btn-secondary"
              disabled={disabled}
              onClick={() => inputRef.current?.click()}
            >
              {showCurrent || file ? t('specialists.replaceImage') : t('specialists.uploadImage')}
            </button>
            {(showCurrent || file) && !removeRequested ? (
              <button type="button" className="btn btn-ghost" disabled={disabled} onClick={onRemove}>
                {t('specialists.removeImage')}
              </button>
            ) : null}
            {file ? (
              <button type="button" className="btn btn-ghost" disabled={disabled} onClick={() => {
                if (inputRef.current) {
                  inputRef.current.value = '';
                }
                onClearPick();
              }}
              >
                {t('specialists.clearImagePick')}
              </button>
            ) : null}
          </div>
        </>
      ) : showCurrent ? (
        <p className="field-hint">{t('specialists.currentImage')}</p>
      ) : null}

      <p id={hintId} className="field-hint">{t('specialists.avatarUploadHint')}</p>
      {error ? <span id={errorId} className="field-error" role="alert">{error}</span> : null}
    </fieldset>
  );
}
